<script setup lang="ts">
import IcEmptyBox from '@/components/Icons/IcEmptyBox.vue'
import AlarmReportAdherenceSection from './AlarmReportAdherenceSection.vue'
import AlarmReportCharts from './Chart/AlarmReportCharts.vue'
import AlarmReportTotalizers from './Totalizer/AlarmReportTotalizers.vue'

import { useAlarmStore } from '@/store/alarm'

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

const { t } = useI18n()

const { getAlarmReportMetrics, hasAlarmReportMetrics } = storeToRefs(useAlarmStore())

type ReportTab = 'overview' | 'adherence'

const reportTab = ref<ReportTab>('overview')

const hasAdherence = computed(() => Boolean(getAlarmReportMetrics.value?.adherence))

watch(hasAdherence, (ok) => {
  if (!ok) reportTab.value = 'overview'
})

function tabButtonClass(active: boolean) {
  return [
    'border-b-2 px-1 pb-3 text-title-xsm2 font-semibold transition-colors',
    active ? 'border-primary text-primary' : 'border-transparent text-gray hover:text-black',
  ].join(' ')
}
</script>

<template>
  <div class="flex flex-col justify-center gap-4 px-2 py-6.5 lg:px-10">
    <div v-if="hasAlarmReportMetrics" class="flex flex-col items-start justify-start gap-4">
      <h1 class="font-satoshi text-title-sm font-bold text-black">
        {{ t('alarm_report.body.result.title') }}
      </h1>
    </div>

    <div v-else class="flex flex-col items-center text-center">
      <IcEmptyBox class="h-90 pb-12 pt-24" />

      <p class="text-gray font-satoshi text-title-xsm font-bold">
        {{ t('alarm_report.body.result.no_data') }}
      </p>
      <p class="font-satoshitext-title-xs text-gray font-medium">
        {{ t('alarm_report.body.result.no_data_verify') }}
      </p>
    </div>

    <template v-if="hasAlarmReportMetrics && hasAdherence">
      <nav class="-mb-px flex flex-wrap gap-6 border-b border-stroke" aria-label="Relatório de alarmes">
        <button
          type="button"
          class="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          :class="tabButtonClass(reportTab === 'overview')"
          @click="reportTab = 'overview'">
          {{ t('alarm_report.body.tabs.overview') }}
        </button>
        <button
          type="button"
          class="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          :class="tabButtonClass(reportTab === 'adherence')"
          @click="reportTab = 'adherence'">
          {{ t('alarm_report.body.tabs.adherence') }}
        </button>
      </nav>

      <div v-show="reportTab === 'overview'" class="flex flex-col gap-6">
        <AlarmReportTotalizers :alarm-report-metrics="getAlarmReportMetrics" />
        <AlarmReportCharts :alarm-report-metrics="getAlarmReportMetrics" />
      </div>

      <div v-show="reportTab === 'adherence'" class="flex flex-col gap-4">
        <AlarmReportAdherenceSection
          v-if="getAlarmReportMetrics.adherence"
          :adherence="getAlarmReportMetrics.adherence"
          :show-section-heading="false"
        />
      </div>
    </template>

    <template v-else-if="hasAlarmReportMetrics">
      <div class="flex flex-col gap-6">
        <AlarmReportTotalizers :alarm-report-metrics="getAlarmReportMetrics" />
        <AlarmReportCharts :alarm-report-metrics="getAlarmReportMetrics" />
      </div>
    </template>
  </div>
</template>
